import { Link } from "react-router";

import { BillingTypeTermValidation, type AdditionalFeeItemInput } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants/external-link";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";
import { QuestionSectionWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/_layouts/item-wrapper";
import { QuestionCardItem } from "@/components/features/items/edit/_shared/ui/components/system/question/card/card-item";
import { QuestionCardItemsWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/card/card-items-wrapper";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import { FormErrorMessage, FormItem } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { SelectField } from "@/components/ui/form/fields/select-field";
import { DatePickerRange } from "@/components/ui/input/date-picker-range";

import { consumptionTaxOptions } from "./hooks/constants";

/**
 * CHILLNN サポート「宿泊税・入湯税や暖房費など固定費を徴収したい」
 * https://support.chillnn.com/faq/host/policy/3c9fffe15c57812380a8c82051428693
 *
 * ベースURLは externalLink.faqSite（dev/prd で自動切替）を使う。
 * パスは Notion ページ ID で構成されるため、日本語タイトルや旧スラッグは使わない。
 */
const LODGING_TAX_SUPPORT_URL = `${externalLink.faqSite}/faq/host/policy/3c9fffe15c57812380a8c82051428693`;

type Props = {
	isEdited: boolean;
	hasError: boolean;

	additionalFees: ForArrayType<AdditionalFeeItemInput[]>;

	onSetName: (input: string, index: number) => void;

	billingTypeTargetOptions: FormOptionType[];
	onSetBillingTypeTarget: (input: string, index: number) => void;

	billingTypeTermOptions: FormOptionType[];
	onSetBillingTypeTerm: (input: string, index: number) => void;

	billingTypeTermValidationOptions: FormOptionType[];
	onSetBillingTypeTermValidationOptions: (input: string, index: number) => void;
	shouldShowTermValidationSettings: (index: number) => boolean;

	onSetApplicableTerm: (input: DateRange, index: number) => void;

	onSetPriceAmount: (input: number | undefined, index: number) => void;

	kidsAgeOptions: FormOptionType[];
	onSetUseKidsPrice: (input: boolean, index: number) => void;
	onSetKidsMaxAge: (input: string, index: number) => void;
	onSetKidsPriceAmount: (input: number | undefined, index: number) => void;

	shouldShowKidsPriceSettings: (index: number) => boolean;

	onSetIsNotDiscountTarget: (input: boolean, index: number) => void;
	onSetIsNotTargetOnDayTrip: (input: boolean, index: number) => void;
	onSetIsExcludeCancelFeeTarget: (input: boolean, index: number) => void;
	onSetIsExcludedConsumptionTax: (input: boolean, index: number) => void;

	append: () => void;
	canAppendItem?: boolean;
	/** 追加できない理由 */
	reasonForCannotAppendItem?: string;

	remove: (index: number) => void;
	canRemoveItem?: boolean;
	/** 削除できない理由 */
	reasonForCannotRemoveItem?: string;

	title?: string;
	guide?: string;
};

export const QuestionAdditionalFee = ({
	isEdited,
	hasError, //

	additionalFees,

	onSetName,

	billingTypeTargetOptions,
	onSetBillingTypeTarget,

	billingTypeTermOptions,
	onSetBillingTypeTerm,

	billingTypeTermValidationOptions,
	onSetBillingTypeTermValidationOptions,
	shouldShowTermValidationSettings,

	onSetApplicableTerm,

	kidsAgeOptions,
	onSetPriceAmount,
	onSetUseKidsPrice,
	onSetKidsMaxAge,
	onSetKidsPriceAmount,

	shouldShowKidsPriceSettings,

	onSetIsExcludeCancelFeeTarget,
	onSetIsNotDiscountTarget,
	onSetIsNotTargetOnDayTrip,
	onSetIsExcludedConsumptionTax,

	append,
	canAppendItem = true,
	reasonForCannotAppendItem = "",

	remove,
	canRemoveItem = true,
	reasonForCannotRemoveItem = "",

	title: _title,
	guide: _guide,
}: Props) => {
	const title = _title || "宿泊税などの設定";
	const guide =
		_guide ||
		"プランや部屋代金とは別で、入湯税や清掃料金など常に自動的にお客様から徴収する料金を設定できます。<br /> 入湯税など、ディスカウント対象に含めない場合は”ディスカウント対象から外す”にチェックを入れてください。 <br />期間限定で徴収したい料金（暖房費など）は、期間を指定することで特定の期間に毎年自動で徴収されます。";

	return (
		<FeatureIslandCategoryQuestionForMain
			required={false}
			title={title}
			guide={guide}
			isEdited={isEdited}
			hasError={hasError}
			inputChildren={
				<>
					<div className="rounded-md border bg-zinc-50 p-3">
						<p className="text-sm text-zinc-600">
							<b>ディスカウントの適用優先順位について:</b>
							<br />
							「ディスカウントの適用対象から外す」にチェックが入っている場合、その設定が優先され、それぞれの追加料金（宿泊税など）には一切のディスカウントが適用されません。
							<br />
							ディスカウント設定側で適用対象にチェックが入っていない場合は、こちらの除外設定に関わらず、ディスカウントは適用されません。
							<br />
							<br />
							<b>料金設定の税込/非課税にする（宿泊税など）設定について:</b>
							<br />
							非課税にした場合、領収書での表示に反映されます。
							<Link
								to={LODGING_TAX_SUPPORT_URL}
								className="text-link-400 underline-offset-4 hover:underline"
								target="_blank"
								viewTransition
							>
								詳しくはこちら
							</Link>
							<br />
						</p>
					</div>
					<QuestionCardItemsWrapper
						appendItem={append}
						disabled={{
							value: !canAppendItem,
							reason: reasonForCannotAppendItem,
						}}
					>
						<FormItem>
							{Boolean(additionalFees.value.length) &&
								additionalFees.value.map((item, index) => {
									return (
										<QuestionCardItem
											key={item.additionalID.value}
											removeItem={() => remove(index)}
											canRemoveItem={canRemoveItem}
											reasonForCannotRemoveItem={reasonForCannotRemoveItem}
										>
											<div className="flex flex-col gap-y-10">
												<QuestionSectionWrapper>
													<InputTextField
														className="w-full"
														value={item.name.value}
														handleChange={(input) => onSetName(input, index)}
														placeholder="入湯税・清掃料金など"
														label="料金名"
														errorMessage={item.name.error}
													/>
												</QuestionSectionWrapper>

												<div className="flex flex-col gap-y-4">
													<QuestionSectionWrapper>
														<SelectField
															label="料金設定"
															value={item.billingTypeTerm.value}
															options={billingTypeTermOptions}
															handleValueChange={(input) =>
																onSetBillingTypeTerm(input, index)
															}
														/>
														<SelectField
															label="　"
															value={item.billingTypeTarget.value}
															options={billingTypeTargetOptions}
															handleValueChange={(input) =>
																onSetBillingTypeTarget(input, index)
															}
														/>
														<div className="flex items-end gap-x-2">
															<InputNumberField
																label="　"
																value={item.price.amount.value}
																errorMessage={item.price.amount.error}
																handleInput={(input) => onSetPriceAmount(input, index)}
															/>
															<p>円</p>
														</div>
														<SelectField
															label="　"
															value={String(item.isExcludedConsumptionTax?.value ?? false)}
															options={consumptionTaxOptions}
															handleValueChange={(input) =>
																onSetIsExcludedConsumptionTax(input === "true", index)
															}
														/>
													</QuestionSectionWrapper>
													{shouldShowKidsPriceSettings(index) && (
														<QuestionSectionWrapper>
															<CheckboxField
																value={item.useKidsPrice?.value ?? false}
																handleChange={(input) => onSetUseKidsPrice(input, index)}
																label="特定の年齢（子ども）の料金を別途設定する"
																errorMessage={item.useKidsPrice?.error}
															/>
														</QuestionSectionWrapper>
													)}
												</div>

												{Boolean(
													item.useKidsPrice?.value && item.kidsPrices?.value?.length,
												) && (
														<QuestionSectionWrapper>
															<p>0歳から</p>
															<SelectField
																value={item.kidsPrices!.value!.at(0)?.kidsMaxAge.value}
																errorMessage={
																	item.kidsPrices?.value?.at(0)?.kidsMaxAge?.error
																}
																options={kidsAgeOptions}
																handleValueChange={(input) => onSetKidsMaxAge(input, index)}
															/>
															<p>は</p>
															<InputNumberField
																value={
																	item.kidsPrices?.value?.at(0)?.price.amount.value ??
																	undefined
																}
																errorMessage={
																	item.kidsPrices?.value?.at(0)?.price.amount.error
																}
																handleInput={(input) => onSetKidsPriceAmount(input, index)}
															/>
															<p>円（税込）</p>
														</QuestionSectionWrapper>
													)}

												<div className="flex flex-col gap-y-2">
													<QuestionSectionWrapper>
														<SelectField
															value={
																item.billingTypeTermValidation?.value ??
																BillingTypeTermValidation.Ordinary
															}
															options={billingTypeTermValidationOptions}
															handleValueChange={(input) =>
																onSetBillingTypeTermValidationOptions(input, index)
															}
															label="制限"
															errorMessage={item.billingTypeTermValidation?.error}
														/>
													</QuestionSectionWrapper>

													{shouldShowTermValidationSettings(index) && (
														<FormItem>
															<DatePickerRange
																hideYear={true}
																selected={{
																	from:
																		item.termValidationApplyStartDt?.value ??
																		undefined,
																	to:
																		item.termValidationApplyEndDt?.value ??
																		undefined,
																}}
																onSelect={(input) => onSetApplicableTerm(input, index)}
															/>
															<FormErrorMessage>
																{item.termValidationApplyStartDt?.error ||
																	item.termValidationApplyEndDt?.error}
															</FormErrorMessage>
														</FormItem>
													)}
												</div>

												<div className="flex flex-col gap-y-4">
													<QuestionSectionWrapper>
														<CheckboxField
															value={item.isExcludeCancelFeeTarget?.value ?? false}
															handleChange={(input) =>
																onSetIsExcludeCancelFeeTarget(input, index)
															}
															label="キャンセルポリシーの適用対象から外す"
															errorMessage={item.isExcludeCancelFeeTarget?.error}
														/>
													</QuestionSectionWrapper>
													<QuestionSectionWrapper>
														<CheckboxField
															value={item.isNotDiscountTarget?.value ?? false}
															handleChange={(input) =>
																onSetIsNotDiscountTarget(input, index)
															}
															label="ディスカウントの適用対象から外す"
															errorMessage={item.isNotDiscountTarget?.error}
														/>
													</QuestionSectionWrapper>
													<QuestionSectionWrapper>
														<CheckboxField
															value={item.isNotTargetOnDayTrip?.value ?? false}
															handleChange={(input) =>
																onSetIsNotTargetOnDayTrip(input, index)
															}
															label="日帰り予約（日帰りプラン）選択時、適用対象から外す"
															errorMessage={item.isNotTargetOnDayTrip?.error}
														/>
													</QuestionSectionWrapper>
												</div>
											</div>
										</QuestionCardItem>
									);
								})}
						</FormItem>
					</QuestionCardItemsWrapper>
				</>
			}
		/>
	);
};

QuestionAdditionalFee.displayName = "QuestionAdditionalFee";
